import { vOnClickOutside } from '@vueuse/components'
import { createPinia } from 'pinia'
import { type AppConfig, createApp } from 'vue'

import '@/assets/styles/main.css'
import RootApp from '@/components/RootApp.vue'
import { type FrappePluginOptions, frappeVuePlugin } from '@/plugins/vuePluginFrappe'
import { i18nextInit, i18nextInstance, i18nextVuePlugin } from '@/plugins/vuePluginI18next'
import router from '@/routers/routerRoot'
import { isEnvironmentStaging, viteBaseUrl } from '@/utilities/environments'
import { handleUnknownError } from '@/utilities/errors'
import { handleFrappeInterceptNewCreateActions } from '@/utilities/handleFrappeInterceptNewCreateActions.ts'

type ApplicationFallbackErrorHandler = NonNullable<AppConfig['errorHandler']>

const customDirectiveClickOutside = vOnClickOutside

const applicationFallbackErrorHandler: ApplicationFallbackErrorHandler = (
  applicationError,
  instance,
  extraInformation,
) => {
  if (applicationError instanceof Error) {
    const fallbackErrorPrefix = 'Application FallbackError: '

    console.error(fallbackErrorPrefix, applicationError.name)
    console.error(applicationError, instance, extraInformation)
  } else {
    handleUnknownError(applicationError)
  }
}

const mountApp = async (): Promise<void> => {
  const testEnvironmentVariable = import.meta.env.VITE_TEST_VARIABLE

  if (testEnvironmentVariable) {
    console.debug('environment variable [VITE_TEST_VARIABLE]:', import.meta.env.VITE_TEST_VARIABLE)
  }

  await i18nextInit().then((isI18nextReady) => {
    if (isI18nextReady) {
      const currentApp = createApp(RootApp)

      // app-level error handler that captures errors from all descendant components
      currentApp.config.errorHandler = applicationFallbackErrorHandler

      // Pinia configuration
      const pinia = createPinia()

      // Pinia use (pinia plugins)
      // pinia.use(frappePiniaPlugin)

      const applicationNameDefault = 'missing_application_name'
      const currentApplicationName = import.meta.env.VITE_APPLICATION_NAME ?? applicationNameDefault
      const frappeApplicationName = `${currentApplicationName} [Frappe API]`
      const frappePluginOptions: FrappePluginOptions = {
        // customHeaders: { 'header-key': 'header value' },
        frappeInstanceUrl: viteBaseUrl,
        name: frappeApplicationName,
      }

      // App use (vue plugins)
      currentApp.use(frappeVuePlugin, frappePluginOptions)
      currentApp.use(pinia)
      currentApp.use(router)
      currentApp.use(i18nextVuePlugin, { i18next: i18nextInstance })

      window.addEventListener('message', handleFrappeInterceptNewCreateActions)

      // add custom directives
      currentApp.directive('click-outside', customDirectiveClickOutside)

      // mount application to index.html
      currentApp.mount('#app')
    } else {
      console.error('Vue App not started!')
    }
  })
}

mountApp()
  .then(() => {
    console.info('mount app success')
  })
  .catch((mountError: unknown) => {
    if (mountError instanceof Error) {
      console.error(mountError.message)
    } else {
      handleUnknownError(mountError)
    }
    console.error('mount app failure')
  })

//derived from vite documentation (https://vitejs.dev/guide/build.html#load-error-handling)
/**
 * For Handling Deployment Errors
 * When a new deployment occurs, the hosting service may delete the assets from previous deployments.
 * As a result, a user who visited your site before the new deployment might encounter an import error.
 * This error happens because the assets running on that user's device are outdated and it tries to
 * import the corresponding old chunk, which is deleted.
 * This event is useful for addressing this situation.
 */
window.addEventListener('vite:preloadError', (viteErrorEvent) => {
  // if (isEnvironmentProduction) {
  //   // prevent an error is thrown (for end user)
  //   viteErrorEvent.preventDefault()
  // }

  if (!viteErrorEvent.defaultPrevented) {
    if (isEnvironmentStaging) {
      // get error information ("payload" contains import error)
      console.error(viteErrorEvent.payload)
    } else {
      window.location.reload() // refresh the page
    }
  }
})
